/**
 * Pure localStorage queue for push-open beacons that fire before an
 * auth session is available (iOS cold-launch case: user taps a push
 * from the lock screen, iOS relaunches the app, the Capacitor
 * `pushNotificationActionPerformed` listener fires within the first
 * event-loop tick, but the better-auth session cookie hasn't been
 * validated yet -> POST /api/me/push-open returns 401 -> open is
 * silently dropped).
 *
 * The queue holds pending beacons; a listener elsewhere flushes them
 * the moment auth resolves with a real user. Every beacon lives in
 * localStorage so it survives an app restart between the tap and
 * the successful auth (also common: user taps push, dismisses app,
 * comes back an hour later + signs in). A 30-day TTL prevents an
 * orphaned queue from growing forever.
 *
 * NO React, NO fetch, NO Capacitor. Testable from root vitest.
 */

const KEY = "assembly.push.openQueue";
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const MAX_ENTRIES = 200; // hard cap to prevent runaway growth

export type QueuedPushOpen = {
  campaignId: string;
  kind: string;
  queuedAt: number;
};

function defaultStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Read the current queue. Never throws (schema drift, private-mode
 * Safari, disabled storage all handled). Filters out entries older
 * than MAX_AGE_MS.
 */
export function readPushOpenQueue(
  storage: Storage | null = defaultStorage(),
  now: number = Date.now(),
): QueuedPushOpen[] {
  if (!storage) return [];
  try {
    const raw = storage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const cutoff = now - MAX_AGE_MS;
    return parsed
      .filter(
        (e): e is QueuedPushOpen =>
          e &&
          typeof e === "object" &&
          typeof e.campaignId === "string" &&
          typeof e.kind === "string" &&
          typeof e.queuedAt === "number" &&
          e.queuedAt >= cutoff,
      )
      .slice(-MAX_ENTRIES);
  } catch {
    return [];
  }
}

/**
 * Enqueue a beacon. Idempotent: same (campaignId, kind) already in
 * the queue is not appended twice (matches the server-side dedup
 * so a double-tap doesn't queue two flushes).
 */
export function enqueuePushOpen(
  entry: { campaignId: string; kind: string; now?: number },
  storage: Storage | null = defaultStorage(),
): void {
  if (!storage) return;
  const now = entry.now ?? Date.now();
  const existing = readPushOpenQueue(storage, now);
  const alreadyQueued = existing.some(
    (e) => e.campaignId === entry.campaignId && e.kind === entry.kind,
  );
  if (alreadyQueued) return;
  const next: QueuedPushOpen[] = [
    ...existing,
    { campaignId: entry.campaignId, kind: entry.kind, queuedAt: now },
  ].slice(-MAX_ENTRIES);
  try {
    storage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* storage full or disabled; a lost queue = worst-case forgotten
       open, not a crash. */
  }
}

/**
 * Remove a single entry (called after a successful POST replay).
 * Matches on (campaignId, kind); ignores unmatched. Reads the raw
 * storage blob rather than going through readPushOpenQueue so the
 * TTL filter doesn't accidentally hide entries we're trying to
 * remove (matters when the caller's clock differs from the enqueue
 * clock, e.g. a queue that survived a very long backgrounded app
 * or a test with a fake `now`).
 */
export function removePushOpenFromQueue(
  match: { campaignId: string; kind: string },
  storage: Storage | null = defaultStorage(),
): void {
  if (!storage) return;
  let existing: QueuedPushOpen[];
  try {
    const raw = storage.getItem(KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return;
    existing = parsed.filter(
      (e): e is QueuedPushOpen =>
        e &&
        typeof e === "object" &&
        typeof e.campaignId === "string" &&
        typeof e.kind === "string" &&
        typeof e.queuedAt === "number",
    );
  } catch {
    return;
  }
  const filtered = existing.filter(
    (e) => !(e.campaignId === match.campaignId && e.kind === match.kind),
  );
  if (filtered.length === existing.length) return;
  try {
    if (filtered.length === 0) storage.removeItem(KEY);
    else storage.setItem(KEY, JSON.stringify(filtered));
  } catch {
    /* fall through; retry next flush */
  }
}

/**
 * Empty the entire queue. Called on definitive "user is signed out
 * with no chance of coming back" (e.g. user tapped sign-out).
 */
export function clearPushOpenQueue(
  storage: Storage | null = defaultStorage(),
): void {
  if (!storage) return;
  try {
    storage.removeItem(KEY);
  } catch {
    /* silent */
  }
}
